import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01IconStroke } from '@/components/icons';
import { T } from '@/util/Localize';
import { WeatherIcons, weatherForDate, type CalendarWeather } from '../cart-weather';
import {
    MONTH_ABBREVIATIONS,
    WEEKDAY_HEADINGS,
    addPick,
    buildPreferenceGrid,
    canAddWeek,
    ordinal,
    pickIndex,
    prunePicks,
    removePick,
    requiredSelections,
    type PreferenceCell,
    type PreferencePick,
    type PreferenceSettings
} from './preference-rules';

/** Legacy's link, word for word (`cart-apptpref.asp` :34). */
export const FURTHER_OUT_LABEL = 'I need something further out';

export interface PreferencePickerProps extends PreferenceSettings {
    /** The picks, in pick order — the first is the first choice. */
    value: readonly PreferencePick[];
    onChange: (picks: PreferencePick[]) => void;
    disabled?: boolean;
    /** Accessible name for the whole control. */
    label?: string;
    /**
     * ROBO-846: the forecast by ISO date and the brand's units. Each day with rain, snow or wind
     * in it gets a small icon beside its date. Absent, the calendar draws exactly as it always did.
     */
    weather?: CalendarWeather;
}

/**
 * Read by a screen reader, not drawn.
 * ★ '1px', not 1: in `sx` a unitless width or height of 1 means 100% (see the same note in
 * `cart-gcal/availability-picker.tsx` — it pushed a phone-width page 131px sideways).
 */
const visuallyHidden = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap'
} as const;

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

/** `Friday, October 9` — the spoken name of a day, used in every slot's accessible name. */
function longDay(cell: PreferenceCell): string {
    return `${T(WEEKDAYS[cell.weekday])}, ${T(MONTHS[cell.month])} ${cell.day}`;
}

/** A slot's spoken name. Legacy draws an unnamed slot as a blank button; it still needs a name. */
function slotLabel(name: string, index: number): string {
    return name.trim() ? name : `${T('Slot')} ${index + 1}`;
}

const columns = { xs: '1fr', md: 'repeat(7, minmax(0, 1fr))' };

/**
 * The appointment-preference calendar — the React counterpart of legacy `cart-apptpref`: some
 * weeks of days, each open day offering the design's named slots ("Morning", "Afternoon", …), of
 * which the customer picks a first, second, third… choice. Nothing is booked: the picks become one
 * line of text (`formatPreferenceText`) that staff read on the order.
 *
 * It is controlled and knows nothing about its host: `value` in, `onChange` out. What the host may
 * do with the picks — when it may save, what it sends — is the host's, using the same rule
 * functions (`requiredSelections`, `formatPreferenceText`).
 *
 * From `md` up it is legacy's seven-column grid. Below that the same days run down the page, one
 * row each, so nothing has to scroll sideways on a phone; days already past are left out there.
 */
export function PreferencePicker({
    today,
    slots,
    weeks,
    weeksMax,
    daysOut,
    minSelections,
    maxSelections,
    daysOfWeek,
    unavailable,
    daysOff,
    weekMask,
    value,
    onChange,
    disabled = false,
    label,
    weather
}: PreferencePickerProps) {
    /** Weeks added with "I need something further out" — legacy `++options.weeks` (:226). */
    const [addedWeeks, setAddedWeeks] = useState(0);
    const shownWeeks = weeks + addedWeeks;
    const countId = useId();
    const groupRef = useRef<HTMLDivElement>(null);

    const grid = useMemo(
        () =>
            buildPreferenceGrid({
                today,
                weeks: shownWeeks,
                daysOut,
                daysOfWeek,
                unavailable,
                daysOff,
                weekMask
            }),
        [today, shownWeeks, daysOut, daysOfWeek, unavailable, daysOff, weekMask]
    );

    // Legacy's redraw drops a pick whose slot is no longer drawn (`cart-apptpref.js` :60, :69) —
    // on the cart, a change of team can close a day that was already picked.
    useEffect(() => {
        const kept = prunePicks(value, grid, slots.length);
        if (kept !== value) onChange([...kept]);
    }, [grid, onChange, slots.length, value]);

    const isFull = value.length >= maxSelections;
    const required = requiredSelections({ minSelections, maxSelections });

    const handleSlot = (date: string, slot: number) => {
        const index = pickIndex(value, date, slot);
        const next =
            index === -1 ? addPick(value, { date, slot }, maxSelections) : removePick(value, index);
        if (next !== value) onChange([...next]);
    };

    const handleAddWeek = () => {
        const next = addedWeeks + 1;
        setAddedWeeks(next);
        // The button is about to disappear: keep focus in the control it belonged to.
        if (!canAddWeek(weeks + next, weeksMax)) groupRef.current?.focus();
    };

    const renderSlots = (cell: PreferenceCell) => {
        const dayName = longDay(cell);
        return (
            <Box
                component="ul"
                sx={{
                    listStyle: 'none',
                    m: 0,
                    p: 0,
                    display: 'flex',
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'row', md: 'column' },
                    gap: 0.75
                }}
            >
                {slots.map((name, slot) => {
                    const index = pickIndex(value, cell.date, slot);
                    const isPicked = index !== -1;
                    const spoken = `${dayName}, ${slotLabel(name, slot)}`;
                    return (
                        <Box component="li" key={slot} sx={{ minWidth: 0, maxWidth: '100%' }}>
                            <Button
                                size="small"
                                variant={isPicked ? 'contained' : 'outlined'}
                                aria-label={
                                    isPicked
                                        ? `${T(ordinal(index))} ${T('choice')}: ${spoken}, ${T('remove')}`
                                        : spoken
                                }
                                data-picked={isPicked ? index + 1 : undefined}
                                // At the maximum only the picks stay live, to be taken back.
                                disabled={disabled || (isFull && !isPicked)}
                                onClick={() => handleSlot(cell.date, slot)}
                                endIcon={
                                    isPicked ? (
                                        <HugeiconsIcon icon={Cancel01IconStroke} size={14} />
                                    ) : undefined
                                }
                                sx={{
                                    width: { xs: 'auto', md: '100%' },
                                    minWidth: { xs: 84, md: 0 },
                                    maxWidth: '100%',
                                    minHeight: 36,
                                    px: 1,
                                    lineHeight: 1.2,
                                    whiteSpace: 'normal',
                                    overflowWrap: 'anywhere'
                                }}
                            >
                                {isPicked ? T(ordinal(index)) : name.trim() ? name : ' '}
                            </Button>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    const renderCell = (cell: PreferenceCell) => {
        const month = T(MONTH_ABBREVIATIONS[cell.month]);
        let body: React.ReactNode = null;
        if (cell.state === 'today') {
            body = (
                <Typography variant="body2" color="text.secondary">
                    {T('Today')}
                </Typography>
            );
        } else if (cell.state === 'open' && slots.length > 0) {
            body = renderSlots(cell);
        } else if (cell.state !== 'past') {
            body = (
                <Typography variant="body2" color="text.disabled">
                    <span aria-hidden="true">—</span>
                    <Box component="span" sx={visuallyHidden}>
                        {T('No times available')}
                    </Box>
                </Typography>
            );
        }

        return (
            <Box
                key={cell.date}
                component="li"
                data-testid={`preference-day-${cell.date}`}
                data-state={cell.state}
                sx={{
                    // A day already gone has nothing to offer; it only holds its place in the grid.
                    display: { xs: cell.state === 'past' ? 'none' : 'flex', md: 'flex' },
                    flexDirection: { xs: 'row', md: 'column' },
                    gap: 1,
                    alignItems: { xs: 'flex-start', md: 'stretch' },
                    minWidth: 0,
                    minHeight: { md: 72 },
                    py: 1,
                    borderTop: 1,
                    borderColor: 'divider'
                }}
            >
                <Typography
                    component="div"
                    variant="subtitle2"
                    color={cell.state === 'past' ? 'text.disabled' : 'text.primary'}
                    sx={{
                        flex: { xs: '0 0 76px', md: '0 0 auto' },
                        pt: { xs: 0.75, md: 0 },
                        lineHeight: 1.2,
                        textAlign: { xs: 'left', md: 'center' },
                        // The icons sit on the date's line and never wrap it (ROBO-846).
                        ...(weather ? { whiteSpace: 'nowrap' } : {})
                    }}
                >
                    <Box component="span" sx={visuallyHidden}>
                        {longDay(cell)}
                    </Box>
                    {/* ROBO-846: right-aligned (Herb, 2026-10-24) - floated, so before the label; outside the
                        aria-hidden label because the icons are a focusable button. On md it shares the date's line. */}
                    {weather && (
                        <Box component="span" sx={{ float: 'right', ml: 0.5 }}>
                            <WeatherIcons
                                day={weatherForDate(weather, cell.date, today)}
                                units={weather.units}
                                precipThreshold={weather.precipThreshold}
                            />
                        </Box>
                    )}
                    <span aria-hidden="true">
                        <Box component="span" sx={{ display: { xs: 'block', md: 'none' } }}>
                            {T(WEEKDAY_HEADINGS[cell.weekday])}
                        </Box>
                        {/* Legacy names the month on the first cell and on every 1st (:45). */}
                        <Box
                            component="span"
                            sx={{
                                display: { xs: 'inline', md: cell.showsMonth ? 'inline' : 'none' }
                            }}
                        >
                            {month}{' '}
                        </Box>
                        {cell.day}
                    </span>
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>{body}</Box>
            </Box>
        );
    };

    return (
        <Box
            ref={groupRef}
            tabIndex={-1}
            role="group"
            aria-label={label ?? T('Appointment preferences')}
            aria-describedby={countId}
            data-testid="preference-picker"
            sx={{ outline: 'none', minWidth: 0 }}
        >
            <Typography
                id={countId}
                role="status"
                variant="body2"
                color="text.secondary"
                data-testid="preference-count"
                sx={{ mb: 1 }}
            >
                {`${value.length} ${T('of')} ${maxSelections} ${T('selected')}`}
                {required > 1 && required < maxSelections
                    ? ` — ${T('choose at least')} ${required}`
                    : ''}
            </Typography>

            <Box
                aria-hidden="true"
                sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: columns,
                    columnGap: 1,
                    pb: 0.5
                }}
            >
                {WEEKDAY_HEADINGS.map(heading => (
                    <Typography
                        key={heading}
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', fontWeight: 600 }}
                    >
                        {T(heading)}
                    </Typography>
                ))}
            </Box>

            <Box
                component="ul"
                sx={{
                    listStyle: 'none',
                    m: 0,
                    p: 0,
                    display: 'grid',
                    gridTemplateColumns: columns,
                    columnGap: 1
                }}
            >
                {grid.flat().map(renderCell)}
            </Box>

            {canAddWeek(shownWeeks, weeksMax) && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Button variant="text" size="small" disabled={disabled} onClick={handleAddWeek}>
                        {T(FURTHER_OUT_LABEL)}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
